import React from 'react';

import { FormInputValidation } from '@candu-ai/forms/document-types';
import { useManageFormFieldValidation } from '@candu-ai/forms/editor-utils';
import { toCssSelector } from '@candu-ai/stylesheet/utils';
import Node from 'craft/components/Node';
import { BuiltInComponents } from 'craft/constants';
import { useEditorNode, useNodeContext } from 'craft/hooks';
import { useFormAncestor, useFormIdFromAncestor } from 'craft/state';
import { CssSelectorId, Resolver, ToolboxComponentSections } from 'craft/types';

import { FormFieldValidationMessage } from './FormFieldValidationMessage';
import { FormLabel } from './FormLabel';
import { getFormFieldNodeRules } from './helpers';
import { TextInputToolbox } from './toolbox/TextInputToolbox';

interface Props {
  formInputId: string;
  formId: string;
  label: string;
  placeholder?: string;
  showLabel?: boolean;
  validation?: FormInputValidation[];
}

export const FormTextarea: Resolver = ({
  formInputId,
  formId,
  label,
  placeholder,
  showLabel,
  validation,
}: Props) => {
  const { id } = useNodeContext();
  const { shouldDisplayErrorState } = useFormAncestor(id);
  const { setProps } = useEditorNode(id);

  useFormIdFromAncestor(formId);

  const {
    validationIndexMapping,
    isRequired,
    onSetAtomicValidationProps,
  } = useManageFormFieldValidation({
    setProps,
    validation,
  });

  const textAreaClassName = toCssSelector('form', {
    output: 'html',
    className: 'inputField',
  });

  const classNames = `${textAreaClassName} ${shouldDisplayErrorState ? ' error' : ''} `.trim();

  return (
    <Node is="div" data-type="form-text-input">
      {/* eslint-disable-next-line jsx-a11y/label-has-for */}
      {showLabel && <FormLabel id={id} required={isRequired} label={label} />}
      <textarea id={formInputId} className={classNames} readOnly placeholder={placeholder} />
      {isRequired && shouldDisplayErrorState && (
        <FormFieldValidationMessage
          nodeId={id}
          validationKey="required"
          onSetProps={(value) => onSetAtomicValidationProps(value, 'required')}
          message={validation?.[validationIndexMapping.required]?.message}
        />
      )}
    </Node>
  );
};

FormTextarea.craft = {
  displayName: 'Form: Textarea',
  name: BuiltInComponents.FormTextarea,
  isCanvas: false,
  rules: getFormFieldNodeRules(),
};

FormTextarea.toolbox = {
  component: [TextInputToolbox],
  schema: {
    [ToolboxComponentSections.Border]: {
      cssSelectorId: CssSelectorId.FormTextArea,
    },
    [ToolboxComponentSections.Shadow]: {
      cssSelectorId: CssSelectorId.FormTextArea,
    },
  },
};

FormTextarea.selectedToolbar = {};
